<template>
	<div class="site-utilization">
        <div class="site-utilization__header">
            <div class="site-utilization__header-content">
                <div class="site-utilization__header-left">
                    <h1 class="text-base font-normal text-foreground m-0">Utilization</h1>
                    <div
                        class="badge"
                        :class="`badge--${site.utilization_color}`"
                        title="Utilization">
                        {{ site.utilization }}%
                    </div>
                </div>
                <themed-select
                    v-if="leases.all.length > 1"
                    v-model="selectedLeaseId"
                    :options="leaseOptions"
                    @change="changeLease"
                    class="site-utilization__lease-select"
                />
            </div>
        </div>
        <div v-if="leases.selectedLease" class="space-y-4">
            <div v-if="showUsage" class="site-utilization__section">
                <div @click="expandUsage = ! expandUsage" class="site-utilization__section-header">
                    <div class="site-utilization__section-title-wrapper">
                        <div 
                            class="site-utilization__chevron"
                            :class="{ 'site-utilization__chevron--collapsed': !expandUsage, 'site-utilization__chevron--expanded': expandUsage }"
                        >
                            <theme-icon icon="chevron-right" type="icons" />
                        </div>
                        <div class="text-sm font-semibold text-foreground m-0">Usage</div>
                    </div>
                    <div class="badge badge--yellow">
                        <span class="badge-value">{{ site.utilization }}%</span>
                    </div>
                </div>
                <div v-if="expandUsage" class="site-utilization__section-content">
                    <div class="site-utilization__section-inner">
                        <!-- Total and Unallocated Row -->
                        <div class="site-utilization__usage-top-row">
                            <div class="site-utilization__usage-card site-utilization__usage-card--blue">
                                <div class="site-utilization__usage-card-value">{{ site.size | number }}</div>
                                <div class="site-utilization__usage-card-label">Total Square Footage</div>
                            </div>
                            <div class="site-utilization__usage-card site-utilization__usage-card--blue">
                                <div class="site-utilization__usage-card-value">{{ unallocatedSquareFootage | number }}</div>
                                <div class="site-utilization__usage-card-label">Unallocated Square Footage</div>
                            </div>
                        </div>

                        <!-- Office, Industrial, Manufacturing Row -->
                        <div class="site-utilization__usage-bottom-row">
                            <!-- Office Space -->
                            <div v-if="leases.selectedLease?.office_percentage > 0" class="site-utilization__usage-card site-utilization__usage-card--blue-slider">
                                <div class="site-utilization__usage-slider-wrapper">
                                    <div class="site-utilization__usage-slider-header">
                                        <span class="site-utilization__usage-slider-label">Office Space</span>
                                        <span class="site-utilization__usage-slider-percent">{{ Math.round(leases.selectedLease.office_percentage) }}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        :value="Math.round(leases.selectedLease.office_percentage)"
                                        disabled
                                        class="themed-slider themed-slider--white themed-slider--blue-track"
                                    />
                                </div>
                                <div class="site-utilization__usage-card-value">{{ site.office_square_footage | number }} <span class="site-utilization__usage-card-unit">Sq. Ft.</span></div>
                            </div>

                            <!-- Industrial Space -->
                            <div v-if="leases.selectedLease?.industrial_percentage > 0" class="site-utilization__usage-card site-utilization__usage-card--green-slider">
                                <div class="site-utilization__usage-slider-wrapper">
                                    <div class="site-utilization__usage-slider-header">
                                        <span class="site-utilization__usage-slider-label">Industrial Space</span>
                                        <span class="site-utilization__usage-slider-percent">{{ Math.round(leases.selectedLease.industrial_percentage) }}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        :value="Math.round(leases.selectedLease.industrial_percentage)"
                                        disabled
                                        class="themed-slider themed-slider--white themed-slider--green-track"
                                    />
                                </div>
                                <div class="site-utilization__usage-card-value">{{ site.industrial_square_footage | number }} <span class="site-utilization__usage-card-unit">Sq. Ft.</span></div>
                            </div>

                            <!-- Manufacturing Space -->
                            <div v-if="leases.selectedLease?.manufacturing_percentage > 0" class="site-utilization__usage-card site-utilization__usage-card--green-slider">
                                <div class="site-utilization__usage-slider-wrapper">
                                    <div class="site-utilization__usage-slider-header">
                                        <span class="site-utilization__usage-slider-label">Manufacturing Space</span>
                                        <span class="site-utilization__usage-slider-percent">{{ Math.round(leases.selectedLease.manufacturing_percentage) }}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        :value="Math.round(leases.selectedLease.manufacturing_percentage)"
                                        disabled
                                        class="themed-slider themed-slider--white themed-slider--green-track"
                                    />
                                </div>
                                <div class="site-utilization__usage-card-value">{{ site.manufacturing_square_footage | number }} <span class="site-utilization__usage-card-unit">Sq. Ft.</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="leases.selectedLease?.office_percentage > 0" class="site-utilization__section">
                <div @click="showOffice = ! showOffice" class="site-utilization__section-header">
                    <div class="site-utilization__section-title-wrapper">
                        <div 
                            class="site-utilization__chevron"
                            :class="{ 'site-utilization__chevron--collapsed': !showOffice, 'site-utilization__chevron--expanded': showOffice }"
                        >
                            <theme-icon icon="chevron-right" type="icons" />
                        </div>
                        <div class="text-sm font-semibold text-foreground m-0">Office</div>
                    </div>

                    <div :class="getUtilizationColor(officeUtilization)">
                        <span class="badge-value">{{ officeUtilization }}%</span>
                        <span class="badge-label">Utilized</span>
                    </div>
                </div>

                <div v-if="showOffice" class="site-utilization__section-content">
                    <div class="site-utilization__section-inner">
                        <div v-if="headCount == 0" class="site-utilization__alert">Office Utilization cannot be calculated until you enter your current Employee Count</div>
                        <div class="site-utilization__office-metrics">
                            <div class="site-utilization__office-metric-card">
                                <div class="site-utilization__office-metric-indicator"></div>
                                <div class="site-utilization__office-metric-value">{{ sites.selectedSite.office_allowable_waste_percentage }}%</div>
                                <div class="site-utilization__office-metric-label">Allowable Waste</div>
                                <input
                                    @change="save"
                                    v-model.number="sites.selectedSite.office_allowable_waste_percentage"
                                    class="themed-slider themed-slider--success"
                                    type="range"
                                    min="0"
                                    max="30"
                                    step="1" />
                                <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                    <InfoIcon identifier="sites-utilizationtab-office-allowablewaste" />
                                </div>
                            </div>

                            <div class="site-utilization__office-metric-card">
                                <div class="site-utilization__office-metric-indicator"></div>
                                <div class="site-utilization__office-metric-value">{{ sites.selectedSite.employees_ideal_square_footage }}</div>
                                <div class="site-utilization__office-metric-label">Ideal Density</div>
                                <input
                                    @change="save"
                                    v-model.number="sites.selectedSite.employees_ideal_square_footage"
                                    class="themed-slider themed-slider--success"
                                    type="range"
                                    min="100"
                                    max="500"
                                    step="5" />
                                <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                    <InfoIcon identifier="sites-utilizationtab-office-idealdensity" />
                                </div>
                            </div>

                            <div class="site-utilization__office-metric-card">
                                <div class="site-utilization__office-metric-value">{{ squareFootagePerEmployee | number }}</div>
                                <div class="site-utilization__office-metric-label">Sq. Ft. Per Employee</div>
                                <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                    <InfoIcon identifier="sites-utilizationtab-office-sq.ft.peremployee" />
                                </div>
                            </div>
                        </div>

                        <div class="mt-8">
                            <employee-counts :site-id="sites.selectedSite.id" :data="sites.selectedSite.employee_counts" :show-chart="false"></employee-counts>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="leases.selectedLease?.industrial_percentage > 0" class="site-utilization__section">
                <div @click="showIndustrial = ! showIndustrial" class="site-utilization__section-header">
                    <div class="site-utilization__section-title-wrapper">
                        <div 
                            class="site-utilization__chevron"
                            :class="{ 'site-utilization__chevron--collapsed': !showIndustrial, 'site-utilization__chevron--expanded': showIndustrial }"
                        >
                            <theme-icon icon="chevron-right" type="icons" />
                        </div>
                        <div class="text-sm font-semibold text-foreground m-0">Industrial</div>
                    </div>

                    <div :class="getUtilizationColor(industrialUtilization)">
                        <span class="badge-value">{{ industrialUtilization }}%</span>
                        <span class="badge-label">Utilized</span>
                    </div>
                </div>

                <div v-if="showIndustrial" class="site-utilization__section-content">
                    <div class="site-utilization__section-inner">
                        <div class="site-utilization__industrial-layout">
                            <!-- Left Column: Metrics and Table -->
                            <div class="site-utilization__industrial-left">
                                <!-- Metrics Grid -->
                                <div class="site-utilization__industrial-metrics">
                                    <div class="site-utilization__industrial-metric">
                                        <div class="site-utilization__industrial-metric-value">{{ sites.selectedSite.rack_max }}x</div>
                                        <div class="site-utilization__industrial-metric-label">Max Racks</div>
                                        <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                            <InfoIcon identifier="sites-utilizationtab-industrial-maxracks" />
                                        </div>
                                    </div>

                                    <div class="site-utilization__industrial-metric">
                                        <div class="site-utilization__industrial-metric-value">{{ sites.selectedSite.industrial_allowable_waste_percentage }}%</div>
                                        <div class="site-utilization__industrial-metric-label">Allowable Waste</div>
                                        <input
                                            @change="save"
                                            v-model.number="sites.selectedSite.industrial_allowable_waste_percentage"
                                            class="themed-slider themed-slider--primary"
                                            type="range"
                                            min="0"
                                            max="30"
                                            step="1" />
                                        <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                            <InfoIcon identifier="sites-utilizationtab-industrial-allowablewaste" />
                                        </div>
                                    </div>
                                </div>

                                <div class="site-utilization__industrial-metrics">
                                    <div class="site-utilization__industrial-metric">
                                        <div class="site-utilization__industrial-metric-value">{{ industrialUsedSpace | number }}</div>
                                        <div class="site-utilization__industrial-metric-label">Used Space</div>
                                        <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                            <InfoIcon identifier="sites-utilizationtab-industrial-usedspace" />
                                        </div>
                                    </div>

                                    <div class="site-utilization__industrial-metric">
                                        <div class="site-utilization__industrial-metric-value">{{ industrialAvailableSpace | number }}</div>
                                        <div class="site-utilization__industrial-metric-label">Available Space</div>
                                        <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                            <InfoIcon identifier="sites-utilizationtab-industrial-availablespace" />
                                        </div>
                                    </div>
                                </div>

                                <!-- Racks Table -->
                                <div v-if="sites.selectedSite.rack_max > 0" class="site-utilization__racks-table-container">
                                    <div class="site-utilization__racks-controls">
                                        <themed-button
                                            v-if="!isEditingRacks"
                                            @click="startEditingRacks"
                                            variant="outline"
                                            size="sm"
                                        >
                                            Edit Racks
                                        </themed-button>
                                        <template v-else>
                                            <themed-button
                                                @click="saveAllRacks"
                                                variant="default"
                                                size="sm"
                                                :disabled="isSavingRacks"
                                            >
                                                <theme-icon v-if="isSavingRacks" icon="loader" type="icons" classes="w-4 h-4 animate-spin" />
                                                <span>{{ isSavingRacks ? 'Saving...' : 'Save All' }}</span>
                                            </themed-button>
                                            <themed-button
                                                @click="cancelEditingRacks"
                                                variant="ghost"
                                                size="sm"
                                                :disabled="isSavingRacks"
                                            >
                                                Cancel
                                            </themed-button>
                                        </template>
                                    </div>
                                    
                                    <themed-table
                                        ref="racksTable"
                                        :columns="racksColumns"
                                        :data="racksTableData"
                                        :inline-editable="true"
                                        edit-mode="bulk"
                                        :external-edit-mode="isEditingRacks"
                                        :pagination="false"
                                        :searchable="false"
                                        :sortable="false"
                                        class="site-utilization__racks-themed-table"
                                        @bulk-save-success="handleRacksSaveSuccess"
                                        @bulk-save-error="handleRacksSaveError"
                                    />
                                </div>
                            </div>

                            <!-- Right Column: Chart -->
                            <div v-if="showIndustrialChart && sites.selectedSite.rack_max > 0" class="site-utilization__industrial-right">
                                <div class="site-utilization__chart-type-selector">
                                    <label class="site-utilization__chart-type-label">Chart Type</label>
                                    <themed-select
                                        v-model="chartType"
                                        :options="chartTypeOptions"
                                        class="site-utilization__chart-type-select"
                                    />
                                </div>
                                <div class="site-utilization__chart-wrapper">
                                    <theme-pie-chart
                                        v-if="chartType === 'pie' || chartType === 'donut'"
                                        :data="industrialChartData"
                                        :donut="chartType === 'donut'"
                                    />
                                    <theme-bar-chart
                                        v-if="chartType === 'bar'"
                                        :data="industrialChartData"
                                        :height="400"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="site-utilization__section">
                <div @click="showWaste = ! showWaste" class="site-utilization__section-header">
                    <div class="site-utilization__section-title-wrapper">
                        <div 
                            class="site-utilization__chevron"
                            :class="{ 'site-utilization__chevron--collapsed': !showWaste, 'site-utilization__chevron--expanded': showWaste }"
                        >
                            <theme-icon icon="chevron-right" type="icons" />
                        </div>
                        <div class="text-sm font-semibold text-foreground m-0">Waste</div>
                    </div>
                </div>
                <div v-if="showWaste" class="site-utilization__section-content">
                    <div class="site-utilization__section-inner">
                        <div class="site-utilization__waste-grid">
                            <div class="site-utilization__waste-card">
                                <label class="site-utilization__waste-label">NPV DISCOUNT %</label>
                                <div class="site-utilization__waste-value">{{ site?.npv_discount_rate ?? 0 }}%</div>
                                <div class="site-utilization__waste-description">Set for the whole portfolio in client settings</div>
                                <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                    <InfoIcon identifier="sites-waste-npvdiscount" />
                                </div>
                            </div>

                            <div class="site-utilization__waste-card">
                                <label class="site-utilization__waste-label">NPV</label>
                                <div class="site-utilization__waste-value">${{ (site?.npv ? site.npv : 0) | number }}</div>
                                <div class="site-utilization__waste-description">Net Present Value</div>
                            </div>

                            <div class="site-utilization__waste-card">
                                <label class="site-utilization__waste-label">NPV PSF</label>
                                <div class="site-utilization__waste-value">${{ site?.npv_psf ? Math.round(site.npv_psf) : 0 }}</div>
                                <div class="site-utilization__waste-description">NPV per square foot</div>
                            </div>

                            <div class="site-utilization__waste-card">
                                <label class="site-utilization__waste-label">NPV PER SEAT</label>
                                <div class="site-utilization__waste-value">${{ (site?.npv_per_seat ? site.npv_per_seat : 0) | number }}</div>
                                <div class="site-utilization__waste-description">NPV per seat</div>
                            </div>
                        </div>
                        
                        <div class="site-utilization__waste-actions">
                            <themed-button
                                @click="save"
                                :disabled="isSaving"
                                :loading="isSaving"
                            >
                                {{ isSaving ? 'Saving...' : 'Save Changes' }}
                            </themed-button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="leases.selectedLease?.manufacturing_percentage > 0" class="site-utilization__section">
                <div @click="showManufacturing = ! showManufacturing" class="site-utilization__section-header">
                    <div class="site-utilization__section-title-wrapper">
                        <div 
                            class="site-utilization__chevron"
                            :class="{ 'site-utilization__chevron--collapsed': !showManufacturing, 'site-utilization__chevron--expanded': showManufacturing }"
                        >
                            <theme-icon icon="chevron-right" type="icons" />
                        </div>
                        <div class="text-sm font-semibold text-foreground m-0">Manufacturing</div>
                    </div>

                    <div :class="getUtilizationColor(manufacturingUtilization)">
                        <span class="badge-value">{{ manufacturingUtilization }}%</span>
                        <span class="badge-label">Utilized</span>
                    </div>
                </div>

                <div v-if="showManufacturing" class="site-utilization__section-content">
                    <div class="site-utilization__section-inner">
                        <div class="site-utilization__manufacturing-metrics">
                            <div class="site-utilization__manufacturing-metric">
                                <div class="site-utilization__manufacturing-metric-value">{{ sites.selectedSite.manufacturing_allowable_waste_percentage }}%</div>
                                <div class="site-utilization__manufacturing-metric-label">Allowable Waste</div>
                                <input
                                    @change="save"
                                    v-model.number="sites.selectedSite.manufacturing_allowable_waste_percentage"
                                    class="themed-slider themed-slider--primary"
                                    type="range"
                                    min="0"
                                    max="30"
                                    step="1" />
                                <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                    <InfoIcon identifier="sites-utilizationtab-manufacturing-allowablewaste" />
                                </div>
                            </div>

                            <div class="site-utilization__manufacturing-metric">
                                <div class="site-utilization__manufacturing-metric-value">{{ sites.selectedSite.manufacturing_used_square_footage | number }}</div>
                                <div class="site-utilization__manufacturing-metric-label">Used Sq. Ft.</div>
                                <input
                                    @change="save"
                                    v-model.number="sites.selectedSite.manufacturing_used_square_footage"
                                    class="themed-slider themed-slider--primary"
                                    type="range"
                                    min="0"
                                    :max="sites.selectedSite.manufacturing_square_footage"
                                    step="1" />
                                <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                    <InfoIcon identifier="sites-utilizationtab-manufacturing-usedspace" />
                                </div>
                            </div>

                            <div class="site-utilization__manufacturing-metric">
                                <div class="site-utilization__manufacturing-metric-value">{{ sites.selectedSite.manufacturing_square_footage | number }}</div>
                                <div class="site-utilization__manufacturing-metric-label">Available Space</div>
                                <div class="site-utilization__info-icon site-utilization__info-icon--top-right">
                                    <InfoIcon identifier="sites-utilizationtab-manufacturing-availablespace" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="none-found">Waiting for Lease Information</div>
	</div>
</template>

<script>
    import { mapState } from 'vuex';
    import ThemedSelect from '../../ThemedSelect.vue';
    import ThemedButton from '../../ThemedButton.vue';
    import ThemeIcon from '../../ThemeIcon.vue';
    import EmployeeCounts from '../EmployeeCounts.vue';

    export default {
        components: {
            ThemedSelect,
            ThemedButton,
            ThemeIcon,
            EmployeeCounts,
            ThemePieChart: () => import('../../charts/ThemePieChart.vue'),
            ThemeBarChart: () => import('../../charts/ThemeBarChart.vue')
        },
    	props: {
            showUsage: {
                type: Boolean,
                default: true
            },

            showIndustrialChart: {
                type: Boolean,
                default: true
            },

            fromReatlas: {
                type: Boolean,
                default: false
            }
    	},

    	data() {
    		return {
                expandUsage: false,
                showOffice: false,
                showIndustrial: false,
                showManufacturing: false,
                showWaste: false,
                isSaving: false,
                selectedLeaseId: null,
                racks: {},
                chartType: 'donut',
                isEditingRacks: false,
                isSavingRacks: false
    		}
    	},

        mounted() {
            this.$nextTick(() => {
                this.selectedLeaseId = this.leases?.selectedLease?.id || null;

                if (_.isEmpty(this.site.rack_data)) {
                    this.generateRackData();
                } else {
                    this.racks = this.site.rack_data;
                }
            });
        },

        computed: {
            ...mapState(['sites']),
            ...mapState(['leases']),

            site: {
                get() {
                    return this.sites.selectedSite;
                },
                set(value) {
                    // Update the site in the Vuex store
                    this.$store.commit('UPDATE_SITE', value);
                }
            },


            headCount() {
                return this.site.head_count;

                // let latestHeadCount = this.site.employee_counts != null
                //     ? this.site.employee_counts[0]
                //     : 0;
                //
                // return _.size(latestHeadCount)
                //     ? parseInt(latestHeadCount['cte']) + parseInt(latestHeadCount['fte'])
                //     : 0;
            },

            squareFootagePerEmployee() {
                return this.site.square_footage_per_employee;

                // if (isNaN(this.site.size) || this.headCount == 0) {
                //     return 0;
                // }
                //
                // return Math.round(this.site.office_square_footage / this.headCount);
            },

            officeUtilization() {
                let idealDensity = parseInt(this.site.employees_ideal_square_footage);

                if (isNaN(idealDensity) || this.headCount == 0) {
                    return 0;
                }

                return Math.round(
                    ((idealDensity / (this.site.office_square_footage / this.headCount)) + (this.site.office_allowable_waste_percentage / 100))
                * 100);
            },

            industrialUtilization() {
                if (this.industrialAvailableSpace == 0) {
                    return 0;
                }

                return Math.round(
                    ((this.industrialUsedSpace / this.industrialAvailableSpace) + (this.site.industrial_allowable_waste_percentage / 100))
                * 100);
            },

            industrialAvailableSpace() {
                return _.sum(
                    _.map(this.racks, (rack) => {
                        let maxSquareFootage = Number.prototype.cleanseInteger(rack.max_square_footage);

                        return isNaN(maxSquareFootage) ? 0 : maxSquareFootage;
                    })
                );
            },

            industrialUsedSpace() {
                return _.sum(
                    _.map(this.racks, (rack) => {
                        let usedSquareFootage = Number.prototype.cleanseInteger(rack.used_square_footage);

                        return isNaN(usedSquareFootage) ? 0 : usedSquareFootage;
                    })
                );
            },

            industrialChartData() {
                return [
                    {
                        label: 'Used',
                        value: this.industrialUsedSpace
                    },
                    {
                        label: 'Remaining',
                        value: this.industrialAvailableSpace - this.industrialUsedSpace
                    }
                ];
            },

            manufacturingUtilization() {
                if (this.site.manufacturing_square_footage == 0) {
                    return 0;
                }

                return Math.round(
                    ((this.site.manufacturing_used_square_footage / this.site.manufacturing_square_footage) + (this.site.manufacturing_allowable_waste_percentage / 100))
                * 100);
            },

            unallocatedSquareFootage() {
                const totalSize = this.site.size || 0;
                const officeSpace = this.site.office_square_footage || 0;
                const industrialSpace = this.site.industrial_square_footage || 0;
                const manufacturingSpace = this.site.manufacturing_square_footage || 0;
                
                return Math.max(0, totalSize - officeSpace - industrialSpace - manufacturingSpace);
            },

            racksColumns() {
                return [
                    {
                        field: 'level',
                        label: 'LEVEL',
                        type: 'text',
                        editable: false,
                        width: '80px',
                        align: 'left'
                    },
                    {
                        field: 'used_square_footage',
                        label: 'USED SF',
                        type: 'number',
                        editable: true,
                        width: '150px',
                        align: 'right'
                    },
                    {
                        field: 'max_square_footage',
                        label: 'AVAIL. SF',
                        type: 'number',
                        editable: true,
                        width: '150px',
                        align: 'right'
                    }
                ];
            },

            racksTableData() {
                return _.map(this.racks, (rack) => {
                    // Parse values to ensure they're numbers, not formatted strings
                    const usedSf = rack.used_square_footage;
                    const maxSf = rack.max_square_footage;
                    
                    // Handle both number and string inputs
                    const parseValue = (val) => {
                        if (val === null || val === undefined || val === '') return 0;
                        if (typeof val === 'number') return val;
                        // Remove commas and parse
                        const cleaned = String(val).replace(/,/g, '');
                        const parsed = parseInt(cleaned);
                        return isNaN(parsed) ? 0 : parsed;
                    };
                    
                    return {
                        id: rack.level,
                        level: rack.level,
                        used_square_footage: parseValue(usedSf),
                        max_square_footage: parseValue(maxSf)
                    };
                });
            },

            chartTextColor() {
                // Get the computed color from CSS variables
                if (typeof window !== 'undefined' && document.documentElement) {
                    // Try to get muted-foreground first (better for charts)
                    // If not available, fall back to foreground
                    const tryVariables = ['--muted-foreground', '--foreground'];
                    
                    for (const varName of tryVariables) {
                        const tempEl = document.createElement('div');
                        tempEl.style.color = `var(${varName})`;
                        tempEl.style.display = 'none';
                        document.body.appendChild(tempEl);
                        
                        const computedColor = getComputedStyle(tempEl).color;
                        document.body.removeChild(tempEl);
                        
                        // Convert RGB to hex
                        if (computedColor && computedColor.startsWith('rgb')) {
                            const rgb = computedColor.match(/\d+/g);
                            if (rgb && rgb.length >= 3) {
                                const hex = rgb.slice(0, 3).map(x => {
                                    const h = parseInt(x).toString(16);
                                    return h.length === 1 ? '0' + h : h;
                                }).join('');
                                
                                // Return the first valid color we find
                                if (hex && hex !== '000000') {
                                    return hex;
                                }
                            }
                        }
                    }
                }
                
                // Ultimate fallback - should never reach here if CSS variables are set
                return '6b7280';
            },

            leaseOptions() {
                return this.leases.all.map(lease => ({
                    label: lease.name,
                    value: lease.id
                }));
            },

            chartTypeOptions() {
                return [
                    { label: 'Pie Chart', value: 'pie' },
                    { label: 'Donut Chart', value: 'donut' },
                    { label: 'Bar Chart', value: 'bar' }
                ];
            },
        },

        methods: {
            calculateRackUsePercentage(rack) {
                let maxSquareFootage = Number.prototype.cleanseInteger(rack.max_square_footage);
                let usedSquareFootage = Number.prototype.cleanseInteger(rack.used_square_footage);

                if (isNaN(usedSquareFootage) || isNaN(maxSquareFootage)) {
                    return 0;
                }

                return Math.round((usedSquareFootage / maxSquareFootage) * 100);
            },

            generateRackData() {
                this.$nextTick(() => {
                    this.racks = _.keyBy(
                        _.map(_.range(0, this.site.rack_max), (value, key) => {
                            return {
                                'level': value + 1,
                                'used_percentage': 0,
                                'used_square_footage': 0,
                                'max_square_footage': Number.prototype.cleanseInteger(this.site.industrial_square_footage)
                            }
                        }),
                    'level');
                })
            },

            changeMaxRacks(numberOfRacks) {
                let currentRackSize = parseInt(_.size(this.racks));

                if (numberOfRacks < currentRackSize) {
                    _.times(currentRackSize - numberOfRacks, () => {
                        delete this.racks[_.findLastKey(this.racks)];
                    });

                    this.racks = _.keyBy(this.racks, 'level');

                } else if (numberOfRacks > currentRackSize) {
                    var currentLevel = currentRackSize;

                    _.times(numberOfRacks - currentRackSize, () => {
                        currentLevel += 1;

                        this.racks[currentLevel] = {
                            'level': currentLevel,
                            'used_percentage': 0,
                            'used_square_footage': 0,
                            'max_square_footage': Number.prototype.cleanseInteger(this.site.industrial_square_footage)
                        };
                    });

                    this.racks = _.keyBy(this.racks, 'level');
                }

                this.save();
            },

            getUtilizationColor(utilization) {
                if (utilization > 100) {
                    return 'badge badge--purple';
                }

                if (utilization >= 90) {
                    return 'badge badge--green';
                }

                if (utilization >= 70) {
                    return 'badge badge--yellow';
                }

                return 'badge badge--red';
            },

            startEditingRacks() {
                this.isEditingRacks = true;
            },

            cancelEditingRacks() {
                this.$refs.racksTable.cancelAllRows();
                this.isEditingRacks = false;
            },

            async saveAllRacks() {
                this.isSavingRacks = true;
                
                // Get the updated data from the table
                // The table's bulk edit will have modified the racksTableData
                // We need to update our racks object with the changes
                const updatedRacks = {};
                
                this.racksTableData.forEach(row => {
                    updatedRacks[row.level] = {
                        level: row.level,
                        used_square_footage: parseInt(row.used_square_footage) || 0,
                        max_square_footage: parseInt(row.max_square_footage) || 0,
                        used_percentage: 0 // Will be calculated on backend if needed
                    };
                });
                
                this.racks = updatedRacks;
                
                // Save to backend
                await this.save();
                
                this.isSavingRacks = false;
                this.isEditingRacks = false;
            },

            handleRacksSaveSuccess({ results }) {
                this.$notify({
                    type: 'success',
                    title: 'Success',
                    text: `${results.length} racks updated successfully`
                });
                this.isEditingRacks = false;
            },

            handleRacksSaveError({ errors }) {
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: `Failed to update ${errors.length} racks`
                });
                console.error('Rack save errors:', errors);
            },

            changeLease() {
                this.expandUsage = true;

                this.leases.selectedLease = _.find(this.site.leases, ['id', this.selectedLeaseId]);
            },

            save() {
                this.site.rack_data = this.racks;
                
                // Create a clean object with only the fields we want to update
                const dataToUpdate = {
                    rack_data: this.racks,
                    _method: 'PATCH' // Ensure we're sending a PATCH request
                };

                this.isSaving = true;
                
                axios.patch('/sites/' + this.site.id, dataToUpdate)
                    .then(response => {
                        this.$eventHub.$emit('site-saved', response.data.site);

                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });
                    })
                    .catch(error => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response?.data?.message || 'An error occurred while saving.'
                        });
                        console.error('Error saving site:', error);
                    })
                    .finally(() => {
                        this.isSaving = false;
                    });
            }
        }
    }
</script>
